import React from "react";
import { useSelector, useDispatch } from "react-redux";
import useGetFormData from "repoV2/features/Common/modules/FormData/utils/useGetFormData";
import { IAppliedCouponCodeDetails } from "repoV2/features/Common/modules/FormData/interfaces/IAppliedCouponCodeDetails";
import { FORM_KEYS } from "repoV2/features/Common/modules/FormData/FormData.constants";
import { PAYMENT_PLAN_TYPE } from "repoV2/constants/listing";
import { getQueryParam } from "repoV2/utils/urls&routing/urlParams";
import {
    LISTING_STATUSES,
    LISTING_TYPES,
} from "repoV2/features/Listing/Listing.constants";
import { callGetPaymentInstallmentsApi } from "repoV2/features/Payments/utils/callGetPaymentInstallmentsApi";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import { IInstallments } from "repoV2/features/Payments/Payments.interface";
import { URL_SEARCH_PARAMS } from "repoV2/constants/urls&routing/urlParams";
import { CHECKOUT_PAGES_ACTION_TYPES } from "../constants/CheckoutPagesReducer";

const useFetchNSetCheckoutPagePartPayments = ({
    listingUuid,
    listingType,
    listingStatus,
    paymentPlanType,
}: {
    listingUuid: string;
    listingType?: number;
    listingStatus: number;
    paymentPlanType?: number;
}) => {
    const dispatch = useDispatch();

    // Part payment does not exist on dynamic links, as dynamic link
    // represents custom payment pages, which have different payment values
    // than default payment page
    const dynamicLink = getQueryParam(URL_SEARCH_PARAMS.DYNAMIC_LINK);

    const shouldFetchInstallments = // there is no need for part payments api call.
        //
        // If it is dynamic payment link, we dont need to fetch part payments.
        // As creator has to define price for each part payment, which is not done when
        // custom price is suppplied in case of dynamic links.
        !dynamicLink &&
        //
        // Incase the listing is not live or it is not of type Qpp or LMS
        listingStatus === LISTING_STATUSES.LIVE &&
        (listingType === LISTING_TYPES.NO_SCHEDULE ||
            listingType === LISTING_TYPES.RECORDED_CONTENT) &&
        // Incase payment plan type is of installment type, else no point to fetch api
        paymentPlanType === PAYMENT_PLAN_TYPE.INSTALLMENTS;

    const priceDetails =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails[listingUuid]?.priceSection
                    ?.data
        ) || {};

    const appliedCouponCodeData = useGetFormData<IAppliedCouponCodeDetails>({
        key: `${FORM_KEYS.listing_details}.${listingUuid}.${FORM_KEYS.applied_coupon_code_details}`,
    }) || { applied: false };

    const { currency_name: currencyName } = priceDetails;
    const {
        applied: appliedCouponCode,
        couponCode: discountCode = "",
        usageCount: appliedCount,
    } = appliedCouponCodeData;

    const getCheckoutPagePaymentInstallments = async () => {
        const response = await callGetPaymentInstallmentsApi({
            listingUuid,
            ...(appliedCouponCode && {
                currency: currencyName,
                discountCode,
                appliedCount,
            }),
        });

        const installmentsList: IInstallments[] =
            response.data?.data?.installments;

        const shouldAllowPartPayments =
            installmentsList.some(
                installment => installment?.installment_price_final > 0
            ) || false;

        dispatch({
            type: CHECKOUT_PAGES_ACTION_TYPES.SET_PART_PAYMENTS_DATA,
            payload: {
                data: {
                    installmentsList,
                    shouldAllowPartPayments:
                        shouldAllowPartPayments && shouldFetchInstallments,
                },
                isFetched: true,
                listingUuid,
            },
        });
    };

    React.useEffect(() => {
        if (shouldFetchInstallments) {
            getCheckoutPagePaymentInstallments();
        }
    }, [discountCode]);
};

export default useFetchNSetCheckoutPagePartPayments;
